import type { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import type { DocMode } from '@blocksuite/affine/blocks';
import { ImportIcon } from '@docnboard/icons/blocksuite/rc';
import { AddSmallIcon } from '@docnboard/icons/rc';

import type { useDocHelper } from '../components/blocksuite/block-suite-page-list/utils';
import type { CreateWorkspaceDialogService } from '../modules/create-workspace';
import { registerAffineCommand } from './registry';

export function registerAffineCreationCommands({
  docHelper,
  t,
  createWorkspaceDialogService,
}: {
  t: ReturnType<typeof useI18n>;
  docHelper: ReturnType<typeof useDocHelper>;
  createWorkspaceDialogService: CreateWorkspaceDialogService;
}) {
  const unsubs: Array<() => void> = [];
  unsubs.push(
    registerAffineCommand({
      id: 'affine:new-page',
      category: 'affine:creation',
      label: t['com.affine.cmdk.affine.new-page'](),
      icon: <AddSmallIcon />,
      keyBinding: BUILD_CONFIG.isElectron
        ? {
            binding: '$mod+N',
            skipRegister: true,
          }
        : undefined,
      run() {
        track.$.cmdk.creation.createDoc({ mode: 'page' });

        docHelper.createDoc('page' as DocMode);
      },
    })
  );

  unsubs.push(
    registerAffineCommand({
      id: 'affine:new-edgeless-page',
      category: 'affine:creation',
      icon: <AddSmallIcon />,
      label: t['com.affine.cmdk.affine.new-edgeless-page'](),
      run() {
        const newDoc = docHelper.createEdgeless();
        if (!newDoc) {
          return;
        }

        track.$.cmdk.creation.createDoc({
          mode: 'edgeless',
        });
      },
    })
  );

  unsubs.push(
    registerAffineCommand({
      id: 'affine:new-workspace',
      category: 'affine:creation',
      icon: <AddSmallIcon />,
      label: t['com.affine.cmdk.affine.new-workspace'](),
      run() {
        track.$.cmdk.workspace.openCreateWorkspace();

        createWorkspaceDialogService.dialog.open('new');
      },
    })
  );
  unsubs.push(
    registerAffineCommand({
      id: 'affine:import-workspace',
      category: 'affine:creation',
      icon: <ImportIcon />,
      label: t['com.affine.cmdk.affine.import-workspace'](),
      preconditionStrategy: () => {
        return BUILD_CONFIG.isElectron;
      },
      run() {
        track.$.cmdk.workspace.openCreateWorkspace({
          control: 'import',
        });

        createWorkspaceDialogService.dialog.open('add');
      },
    })
  );

  return () => {
    unsubs.forEach(unsub => unsub());
  };
}
